import { useMutation, useQueryClient } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';
import type {
  PatchJobsId200,
  PatchJobsIdMutationRequest,
  PutJobsIdSteps200,
  PutJobsIdStepsMutationRequest,
} from '@eureca/recruiter-api-sdk/types';
import { withNormalizedApiDates } from '@/lib/api-dates';
import { jobByIdQueryKey } from './job-by-id-query';

export class JobMutationError extends Error {
  readonly name = 'JobMutationError';
  constructor(
    message: string,
    readonly status: number,
    /**
     * Body do response do BFF/Core quando a mutation falha. Inclui
     * envelopes específicos como `{ code: 'invalid_job_location', invalid: [...] }`
     * que o caller usa para renderizar toasts contextuais.
     */
    readonly details?: Record<string, unknown>
  ) {
    super(message);
  }
}

/**
 * Mutation for `PATCH /jobs/:id`. On success refreshes the cached job
 * detail (so the main edit page re-renders the new name/status) and the list
 * (so the job row reflects the change). The caller is responsible for any
 * navigation / toasting on success or error.
 */
export function usePatchJob(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: PatchJobsIdMutationRequest): Promise<PatchJobsId200> => {
      const res = await fetchClient<PatchJobsId200>({
        method: 'PATCH',
        url: `/jobs/${jobId}`,
        // EU-402 RN-01/RN-03 — normalize startDate/endDate (the job instants) to
        // ISO 8601 before sending. The date-only module dates go through
        // `PUT /jobs/:id/steps`, outside this payload, so they are unaffected.
        data: withNormalizedApiDates(patch),
      });
      if (res.error) {
        // `res.error.error` é o body retornado pelo BFF — repassa
        // verbatim via `mapCoreSdkGatewayError`. Em 422 com lookup falha
        // EU-159 vem `{ error, code: 'invalid_job_location', invalid: [...] }`.
        const body = (res.error as { error?: unknown }).error;
        const details =
          body && typeof body === 'object' ? (body as Record<string, unknown>) : undefined;
        throw new JobMutationError(
          `Failed to update job (status ${res.error.status})`,
          res.error.status,
          details
        );
      }
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(jobByIdQueryKey(jobId), data);
      void queryClient.invalidateQueries({ queryKey: ['recruiter', 'jobs'] });
    },
  });
}

/**
 * Mutation for `PUT /jobs/:id/steps` — replace-set the recruitment flow
 * (`process_step` + `module`) for a job in a single transaction.
 *
 * Post trail→job absorption: editing the flow does NOT go through `PATCH
 * /jobs/:id` anymore (which now strips `phases` because the column is
 * gone). Use this mutation from `JobFlowPage` whenever the operator
 * reorders / adds / edits / removes a phase or stage.
 *
 * On success the job detail cache is invalidated (not patched in
 * place) — the new shape carries hydrated `phases` synthesized server-
 * side from the freshly written `process_step` + `module` rows, so a
 * refetch is the cleanest path back to a consistent view.
 */
export function usePutJobSteps(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // MAP-086 (QA EU-764, 24/09) — sem rede, FALHAR, não pausar. No padrão
    // (`networkMode: 'online'`) o TanStack Query estaciona a mutation enquanto
    // o navegador está offline: o botão "Adicionar etapa" ficava girando sem
    // fim, sem erro e sem saída. Falhando na hora, o consultor vê "Não foi
    // possível salvar" e tenta de novo pelo mesmo botão quando a rede voltar.
    networkMode: 'always',
    mutationFn: async (body: PutJobsIdStepsMutationRequest): Promise<PutJobsIdSteps200> => {
      const res = await fetchClient<PutJobsIdSteps200>({
        method: 'PUT',
        url: `/jobs/${jobId}/steps`,
        data: body,
      });
      if (res.error) {
        const body = (res.error as { error?: unknown }).error;
        const details =
          body && typeof body === 'object' ? (body as Record<string, unknown>) : undefined;
        throw new JobMutationError(
          `Failed to update job steps (status ${res.error.status})`,
          res.error.status,
          details
        );
      }
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobByIdQueryKey(jobId) });
      void queryClient.invalidateQueries({ queryKey: ['recruiter', 'jobs'] });
    },
  });
}
